import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Trash2, Package, Share2, ShoppingCart, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePicks, isPickComplete } from '../../context/PicksContext';
import { useCart } from '../../context/CartContext';
import { slideInRight, drawerBackdrop } from '../../animations/variants';
import { buildShareUrl, MAX_PICKS } from '../../utils/picksShare';

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch { /* fall through */ }
  }
  // Fallback: hidden textarea + execCommand
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function PicksDrawer() {
  const { state, closePicks, removePick, clearPicks, totalPicks, requestConfigure } = usePicks();
  const { addItem, openCart } = useCart();

  const handleShare = async () => {
    if (state.picks.length === 0) return;
    const url = buildShareUrl(state.picks);
    const n = state.picks.length;
    const shareData: ShareData = {
      title: 'My Picks — Northdale Medical Supplies',
      text: `Check out ${n === 1 ? 'this pick' : `these ${n} picks`} from Northdale Medical Supplies`,
      url,
    };
    // Try native share sheet first (iOS/Android + some desktop browsers)
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        const canShare = typeof navigator.canShare !== 'function' || navigator.canShare(shareData);
        if (canShare) {
          await navigator.share(shareData);
          return; // user completed or cancelled — either way, done
        }
      } catch (err: any) {
        // AbortError = user cancelled, don't fall back
        if (err && err.name === 'AbortError') return;
        // other errors fall through to clipboard
      }
    }
    // Fallback: copy to clipboard
    const ok = await copyToClipboard(url);
    if (ok) {
      toast.success('Link copied — paste it anywhere to share!', { icon: '🔗' });
    } else {
      window.prompt('Copy this share link:', url);
    }
  };

  const handleMoveAllToCart = () => {
    const complete = state.picks.filter((p) => isPickComplete(p) && p.variant);
    const needsConfig = state.picks.length - complete.length;
    complete.forEach((p) => p.variant && addItem(p.product, p.variant));
    // Remove only the complete picks; bare ones stay so user can configure them
    complete.forEach((p) => removePick(p.pickId));
    closePicks();
    setTimeout(() => openCart(), 200);
    if (complete.length > 0) {
      toast.success(
        needsConfig > 0
          ? `${complete.length} moved to cart — ${needsConfig} need configuring`
          : `${complete.length} item${complete.length !== 1 ? 's' : ''} moved to cart`,
        { icon: '🛒' }
      );
    } else if (needsConfig > 0) {
      toast('None are configured yet — tap each to set options', { icon: '⚙️' });
    }
  };

  const handleClearAll = () => {
    if (state.picks.length === 0) return;
    clearPicks();
    toast('Picks cleared', { icon: '🧹' });
  };

  return (
    <AnimatePresence>
      {state.isOpen && (
        <>
          <motion.div
            variants={drawerBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={closePicks}
          />
          <motion.div
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <Heart size={20} className="text-[#7B68CC]" fill="currentColor" />
                <h2 className="font-bold text-ndms-navy text-lg">My Picks</h2>
                {totalPicks > 0 && (
                  <span className="bg-[#7B68CC] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {totalPicks}
                  </span>
                )}
              </div>
              <button
                onClick={closePicks}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Close My Picks"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
              {state.picks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-20 h-20 bg-[#7B68CC]/10 rounded-2xl flex items-center justify-center mb-4">
                    <Heart size={32} className="text-[#7B68CC]/40" />
                  </div>
                  <p className="font-semibold text-gray-600 mb-1">No picks yet</p>
                  <p className="text-sm text-gray-400 max-w-[260px]">
                    Heart products you like to save them here — then share your list with a friend.
                  </p>
                  <button
                    onClick={closePicks}
                    className="mt-6 text-[#7B68CC] font-semibold text-sm hover:underline"
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                state.picks.map((pick) => {
                  const complete = isPickComplete(pick);
                  const variantDesc = pick.variant?.variantDescription || [
                    pick.selections.color,
                    pick.selections.compressionLevel ? `${pick.selections.compressionLevel} mmHg` : null,
                    pick.selections.style,
                    pick.selections.size ? `Size ${pick.selections.size}` : null,
                    pick.selections.gender,
                  ].filter(Boolean).join(', ');
                  return (
                    <motion.div
                      key={pick.pickId}
                      layout
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 30 }}
                      className="flex gap-3 bg-gray-50 rounded-2xl p-3"
                    >
                      <div className="w-16 h-16 bg-ndms-cream rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {pick.product.imageUrl ? (
                          <img src={pick.product.imageUrl} alt={pick.product.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package size={20} className="text-ndms-navy/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#7B68CC] mb-0.5">{pick.product.brand}</div>
                        <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{pick.product.name}</div>
                        {variantDesc ? (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{variantDesc}</div>
                        ) : (
                          <div className="text-xs text-amber-600 mt-0.5">Not configured yet</div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {complete && pick.variant ? (
                            <button
                              onClick={() => {
                                if (!pick.variant) return;
                                addItem(pick.product, pick.variant);
                                removePick(pick.pickId);
                                toast.success('Moved to cart', { icon: '🛒' });
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold bg-ndms-navy text-white hover:bg-[#7B68CC] transition-colors py-1.5 rounded-lg"
                            >
                              <ShoppingCart size={12} /> Move to cart
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                requestConfigure(pick.product, pick.selections, pick.pickId);
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold bg-white border border-[#7B68CC] text-[#7B68CC] hover:bg-[#7B68CC] hover:text-white transition-colors py-1.5 rounded-lg"
                            >
                              <Settings size={12} /> Configure & add
                            </button>
                          )}
                          <button
                            onClick={() => removePick(pick.pickId)}
                            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg"
                            aria-label="Remove pick"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {state.picks.length > 0 && (() => {
              const completeCount = state.picks.filter(isPickComplete).length;
              const hasComplete = completeCount > 0;
              return (
                <div className="border-t border-gray-100 px-5 py-4 space-y-2.5">
                  <motion.button
                    whileTap={hasComplete ? { scale: 0.97 } : undefined}
                    onClick={hasComplete ? handleMoveAllToCart : undefined}
                    disabled={!hasComplete}
                    className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl font-bold text-base transition-all ${
                      hasComplete
                        ? 'bg-ndms-navy text-white hover:bg-[#7B68CC] shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    title={hasComplete ? 'Add configured picks to your cart' : 'Configure picks first to add them to cart'}
                  >
                    <ShoppingCart size={18} />
                    {hasComplete
                      ? completeCount === state.picks.length
                        ? 'Add All to Cart'
                        : `Add ${completeCount} to Cart`
                      : 'Configure picks first'}
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleShare}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm bg-[#7B68CC] text-white hover:brightness-110 shadow-md hover:shadow-lg transition-all"
                  >
                    <Share2 size={16} />
                    Share My Picks
                  </motion.button>
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span>{totalPicks} of {MAX_PICKS} picks used</span>
                    <button
                      onClick={handleClearAll}
                      className="font-semibold hover:text-red-500 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
